import { db } from "../../../../../firebaseConfig";
import { auth } from "../../../../../firebaseConfig";
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  query,
  orderBy,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { getDisplayName } from "../../../../../utils/userDisplay.js";

/**
 * Save comment to user's saved collection
 * @param {Object} comment - Comment to save
 */
export const saveComment = async (comment) => {
  try {
    const { currentUser } = auth;
    if (!currentUser) throw new Error("No authenticated user");

    if (!comment?.id) throw new Error("Invalid comment payload: missing id");

    const savedCommentRef = doc(
      db,
      "users",
      currentUser.uid,
      "savedComments",
      comment.id,
    );
    const payload = {
      id: comment.id,
      postId: comment.postId,
      userId: comment.userId || null,
      username: getDisplayName({
        candidates: [comment?.username, comment?.displayName, comment?.name],
      }),
      profession: comment.profession || comment.userProfession || "",
      photoURL: comment.photoURL || comment.userPhotoURL || "",
      text: comment.text || comment.content || "",
      timestamp: comment.timestamp || serverTimestamp(),
      likes: typeof comment.likes === "number" ? comment.likes : 0,
      replyCount:
        typeof comment.replyCount === "number" ? comment.replyCount : 0,
      savedAt: serverTimestamp(),
    };
    await setDoc(savedCommentRef, payload);
  } catch (error) {
    console.error("Error saving comment:", error);
    throw error;
  }
};

/**
 * Unsave comment from user's saved collection
 * @param {string} commentId - Comment ID
 */
export const unsaveComment = async (commentId) => {
  try {
    const { currentUser } = auth;
    if (!currentUser) throw new Error("No authenticated user");
    if (!commentId) throw new Error("No commentId provided");

    const savedCommentRef = doc(
      db,
      "users",
      currentUser.uid,
      "savedComments",
      commentId,
    );
    await deleteDoc(savedCommentRef);
  } catch (error) {
    console.error("Error unsaving comment:", error);
    throw error;
  }
};

/**
 * Get user's saved comments
 * @returns {Promise<Array>} - Saved comments
 */
export const getSavedComments = async () => {
  try {
    const { currentUser } = auth;
    if (!currentUser) throw new Error("No authenticated user");

    const savedCommentsRef = collection(
      db,
      "users",
      currentUser.uid,
      "savedComments",
    );
    const q = query(savedCommentsRef, orderBy("savedAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      const parseTs = (v) =>
        v?.toDate?.() ??
        (v instanceof Date
          ? v
          : typeof v === "string"
            ? new Date(v)
            : undefined);
      return {
        id: docSnap.id,
        ...data,
        timestamp: parseTs(data.timestamp),
        savedAt: parseTs(data.savedAt),
      };
    });
  } catch (error) {
    console.error("Error getting saved comments:", error);
    throw error;
  }
};
